import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdImage, MdPictureAsPdf } from "react-icons/md";
import AIGeneratedGraph from "src/common/components/AIGeneratedGraph/AIGeneratedGraph";
import { PlanPhase, ScenariaPlan, ScenariaPlanScene } from "src/model/model";
import { IMedia } from "src/model/unityObject";
import { generateHashFromString } from "../utils/generateHashFromString";
import { composeB2Prompt, resolveTrunk } from "../utils/renderPlanToMarkdown";
import serializePlanToScenarioMarkdown from "../utils/serializePlanToScenarioMarkdown";
import { baseUrl } from "src/api";
import InteractionDetails from "./interactionDetails/InteractionDetails";

interface GenerationRecapProps {
  plan: ScenariaPlan;
  phase: PlanPhase;
  progressMessage: string | null;
  sourceMedia: IMedia[];
  lastGeneratedProjectGroupId?: number | null;
  isOpeningModule: boolean;
  onOpenModule: () => void;
  onNewScenario: () => void;
  // Unix ms captured when the user kicked off generation. Null when not
  // tracked (e.g., after a page reload mid-generation). Used to render an
  // elapsed-time chip + soft ETA.
  generationStartedAt: number | null;
}

// "1 min 32 s" / "47 s" / "32 s". Always rounds down to whole seconds.
const formatDuration = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s} s`;
  return `${m} min ${s.toString().padStart(2, "0")} s`;
};

// Mirror of PlanPreview's override: the LLM emits "AvatarAIPersona"
// (PascalCase, matches content_types.title) but the app surfaces the
// same row as "People AI". Single-entry table — extend if more types
// drift.
const INTERACTION_DISPLAY_OVERRIDES: Readonly<Record<string, string>> = Object.freeze({
  AvatarAIPersona: "People AI",
});

// Interaction types that legitimately have no static recap drill-down because
// their content is produced live during the VR session (e.g. the avatar's
// debrief is generated at runtime from the learner's performance). For these
// we show an explicit "generated at runtime" note instead of the generic
// "details unavailable" fallback.
const RUNTIME_GENERATED_TYPES: ReadonlySet<string> = new Set(["AvatarAISessionDebrief"]);

const formatInteractionLabel = (raw: string): string => {
  if (typeof raw !== "string" || raw.length === 0) return "";
  if (!Object.prototype.hasOwnProperty.call(INTERACTION_DISPLAY_OVERRIDES, raw)) {
    return raw;
  }
  const override = INTERACTION_DISPLAY_OVERRIDES[raw];
  return typeof override === "string" && override.length > 0 ? override : raw;
};

const isImageMedia = (m: IMedia | undefined | null): boolean =>
  typeof m?.type === "string" && m.type.toLowerCase().startsWith("image");

const mediaLabel = (m: IMedia | undefined | null, fallbackId: number): string => {
  const name = (m?.name && m.name.trim()) || (m?.filename && m.filename.trim());
  return name || `#${fallbackId}`;
};

const formatNumber = (n: number): string => {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k.toFixed(k >= 10 ? 0 : 1)}k`;
  }
  return String(n);
};

// Mirrors apps/backend-strapi/src/api/gpt-conversation/utils/renderPlanToMarkdown.js:
// B2 path (sceneVisualVariation composed with shared trunk) → legacy
// blockadePromptSeed → image description → scene.summary fallback (only
// reachable for malformed plans; render path proper never falls this far).
const resolveScenePrompt = (scene: ScenariaPlanScene, plan: ScenariaPlan): string => {
  if (
    typeof scene.sceneVisualVariation === "string" &&
    scene.sceneVisualVariation.trim().length > 0
  ) {
    return composeB2Prompt(resolveTrunk(plan), scene.sceneVisualVariation.trim());
  }
  if (scene.blockadePromptSeed && scene.blockadePromptSeed.length > 0) {
    return scene.blockadePromptSeed;
  }
  const refIds = scene.attachedImageRefIds || [];
  const firstRef = (plan.attachedImages || []).find(
    (img) => refIds.includes(img.mediaId) && !img.failed && img.description,
  );
  if (firstRef) {
    return firstRef.description;
  }
  return scene.summary;
};

// Per-interaction content row from the scenaria-recap endpoint.
interface RecapInteraction {
  contentId: number;
  contentName: string;
  contentType: string;
  contentTypeModelUid: string | null;
  // Named `contentData` (not `data`) deliberately: Strapi's response transform
  // unwraps a nested `data` key, which would strip the sibling fields.
  contentData: Record<string, unknown> | null;
}

interface RecapScene {
  sceneId: number;
  sceneName: string;
  projectId: number;
  projectName: string;
  interactions: RecapInteraction[];
}

const SceneDetails: React.FC<{
  scene: ScenariaPlanScene;
  sceneIndex: number;
  plan: ScenariaPlan;
  mediaById: Map<number, IMedia>;
  phase: PlanPhase;
  recapData: RecapScene[] | null;
  recapLoading: boolean;
  onRequestRecap: () => void;
}> = ({ scene, sceneIndex, plan, mediaById, phase, recapData, recapLoading, onRequestRecap }) => {
  const { t } = useTranslation();
  const gr = "pages.chat.generationRecap";
  const id = "pages.chat.interactionDetails";
  const meta = scene.generationMetadata;

  const attachedImages = plan.attachedImages;
  const refIds = scene.attachedImageRefIds || [];
  const refDescriptions = (attachedImages || []).filter((img) => refIds.includes(img.mediaId));
  const hasRefs = refDescriptions.length > 0;

  // Post-submission, meta.blockadePrompt is the prompt actually sent (same
  // computation server-side). Pre-submission, surface the locally-resolved
  // value so the user can see what will be sent.
  const displayPrompt = meta?.blockadePrompt || resolveScenePrompt(scene, plan);

  const isVideo = scene.mediaType === "video360";
  const isFailed = meta?.status === "error";

  const statusLabel = (() => {
    if (isVideo) return null;
    // Legacy conversations generated before per-scene generationMetadata
    // was persisted: the module IS generated, just untraceable at the
    // scene level. Don't slap "non généré" on it.
    if (!meta || !meta.status) {
      if (phase === "generated") return null;
      return t(`${gr}.statusUnsubmitted`);
    }
    if (meta.status === "pending") return t(`${gr}.statusPending`);
    if (meta.status === "complete") return t(`${gr}.statusComplete`);
    if (meta.status === "error") return t(`${gr}.statusError`);
    return null;
  })();

  const statusClass =
    meta?.status === "complete"
      ? "text-green-700"
      : meta?.status === "error"
      ? "text-red-700"
      : "text-gray-500";

  const [promptOpen, setPromptOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [interactionsOpen, setInteractionsOpen] = useState(false);
  // Per-interaction drill-down state
  const [openRecapDetails, setOpenRecapDetails] = useState<Set<number>>(new Set());

  const interactionsWithIntent = useMemo(
    () => (scene.interactions || []).filter((i) => i && typeof i.type === "string"),
    [scene.interactions],
  );

  // Resolve recap content for this scene by matching scene title.
  // The recap endpoint returns scenes keyed by DB scene name which
  // matches the plan's scene.title (set at createProjectFromInsertionPlan).
  const sceneRecap = useMemo(() => {
    if (!recapData) return null;
    return recapData.find((s) => s.sceneName === scene.title) || null;
  }, [recapData, scene.title]);

  // Match each plan interaction to its recap row by canonical content TYPE,
  // not contentName: contentName is overridden at creation for some types
  // (e.g. AvatarAIPersona -> "People AI"), so a name match silently fails.
  // Matches are consumed so repeated types in one scene map to distinct rows,
  // and scene navigators (present in the recap but not the plan) are ignored.
  const recapMatches = useMemo<(RecapInteraction | null)[]>(() => {
    const pool = [...(sceneRecap?.interactions || [])];
    return interactionsWithIntent.map((inter) => {
      const idx = pool.findIndex((ri) => ri && ri.contentType === inter.type);
      return idx === -1 ? null : pool.splice(idx, 1)[0];
    });
  }, [sceneRecap, interactionsWithIntent]);

  const toggleRecapDetail = useCallback(
    (interactionIndex: number) => {
      // Trigger fetch on first expand of any interaction
      if (!recapData && !recapLoading) {
        onRequestRecap();
      }
      setOpenRecapDetails((prev) => {
        const next = new Set(prev);
        if (next.has(interactionIndex)) {
          next.delete(interactionIndex);
        } else {
          next.add(interactionIndex);
        }
        return next;
      });
    },
    [recapData, recapLoading, onRequestRecap],
  );

  return (
    <div
      className={`rounded border ${
        isFailed ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
      } p-2`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
        <span className="font-medium text-gray-700">
          {t(`${gr}.sceneNumber`, { n: sceneIndex + 1 })} — {scene.title}
        </span>
        {statusLabel && (
          <span className={`inline-flex items-center gap-1 ${statusClass}`}>
            <span>·</span>
            {meta?.status === "pending" && (
              <span
                className="inline-block w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin"
                aria-hidden
              />
            )}
            <span>{statusLabel}</span>
          </span>
        )}
        {meta?.skyboxStyleName && <span className="text-gray-500">· {meta.skyboxStyleName}</span>}
      </div>

      {isVideo && <p className="mt-1 text-[11px] text-blue-700">{t(`${gr}.video360Skipped`)}</p>}

      {isFailed && meta?.errorMessage && (
        <p className="mt-1 text-[11px] text-red-700">{meta.errorMessage}</p>
      )}

      {!isVideo && (
        <div className="mt-2 space-y-1.5 text-[11px] text-gray-700">
          {interactionsWithIntent.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setInteractionsOpen((v) => !v)}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
              >
                <span>{interactionsOpen ? "▾" : "▸"}</span>
                <span className="font-medium">
                  {t(`${gr}.interactionsLabel`, {
                    n: interactionsWithIntent.length,
                  })}
                </span>
              </button>
              {interactionsOpen && (
                <div className="mt-1 pl-3 space-y-1">
                  {interactionsWithIntent.map((inter, j) => {
                    const isDetailOpen = openRecapDetails.has(j);
                    const recapInter = recapMatches[j] || null;
                    const hasRecapData =
                      recapInter?.contentData && Object.keys(recapInter.contentData).length > 0;

                    return (
                      <div key={`inter-${j}`}>
                        <div
                          className="grid gap-x-2 items-center text-[11px] text-gray-700"
                          style={{ gridTemplateColumns: "auto auto 1fr" }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleRecapDetail(j)}
                            className="self-center text-gray-400 hover:text-gray-600 text-[10px] w-3"
                            aria-label={isDetailOpen ? t(`${id}.collapse`) : t(`${id}.expand`)}
                          >
                            {isDetailOpen ? "▾" : "▸"}
                          </button>
                          <span className="self-center justify-self-start text-center text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            {formatInteractionLabel(inter.type)}
                          </span>
                          <span className="self-center leading-snug">{inter.intent}</span>
                        </div>
                        {isDetailOpen && (
                          <div className="ml-5 mt-1 pl-2 border-l-2 border-gray-200">
                            {recapLoading && !recapData ? (
                              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            ) : hasRecapData && recapInter?.contentData ? (
                              <InteractionDetails
                                type={inter.type}
                                mode="recap"
                                data={recapInter.contentData}
                              />
                            ) : (
                              <p className="text-[11px] text-gray-400 italic">
                                {RUNTIME_GENERATED_TYPES.has(inter.type)
                                  ? t(`${id}.runtimeGenerated`)
                                  : t(`${id}.unavailable`)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => setPromptOpen((v) => !v)}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
            >
              <span>{promptOpen ? "▾" : "▸"}</span>
              <span className="font-medium">{t(`${gr}.promptLabel`)}</span>
            </button>
            {promptOpen && (
              <p className="mt-1 pl-3 whitespace-pre-wrap text-gray-700">{displayPrompt}</p>
            )}
          </div>

          {hasRefs && (
            <div>
              <button
                type="button"
                onClick={() => setRefOpen((v) => !v)}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
              >
                <span>{refOpen ? "▾" : "▸"}</span>
                <span className="font-medium">
                  {t(`${gr}.refDescriptionLabel`, { n: refDescriptions.length })}
                </span>
              </button>
              {refOpen && (
                <ul className="mt-1 pl-3 space-y-1">
                  {refDescriptions.map((img, i) => {
                    const media = mediaById.get(img.mediaId);
                    const label = mediaLabel(media, img.mediaId);
                    return (
                      <li key={`scene-ref-${img.mediaId}-${i}`}>
                        {img.failed ? (
                          <span className="text-red-600 inline-flex items-center gap-1">
                            <MdImage size={12} className="shrink-0" />
                            <span>{label} — </span>
                            <span>{t(`${gr}.refFailed`, { id: img.mediaId })}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-start gap-1">
                            <MdImage size={12} className="shrink-0 mt-0.5 text-gray-500" />
                            <span>
                              <span className="text-gray-700 font-medium">{label}</span>
                              <span className="text-gray-500"> — </span>
                              <span>{img.description}</span>
                            </span>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GenerationRecap: React.FC<GenerationRecapProps> = ({
  plan,
  phase,
  progressMessage,
  sourceMedia,
  lastGeneratedProjectGroupId,
  isOpeningModule,
  onOpenModule,
  onNewScenario,
  generationStartedAt,
}) => {
  const { t } = useTranslation();
  const gr = "pages.chat.generationRecap";

  const imageScenes = (plan.sceneOutline || []).filter((s) => s.mediaType === "image360");
  const scenesWithMeta = imageScenes.filter((s) => s.generationMetadata);
  const completedSkyboxes = scenesWithMeta.filter(
    (s) => s.generationMetadata?.status === "complete",
  );
  const erroredSkyboxes = scenesWithMeta.filter((s) => s.generationMetadata?.status === "error");

  const attached = plan.attachedImages || [];
  const successfulRefs = attached.filter((a) => !a.failed);
  const failedRefs = attached.filter((a) => a.failed);

  // Build a media-by-id lookup once. Used for filename resolution in both
  // the top-level attachments list and the per-scene caret. Falls back to
  // "#mediaId" when the media has been deleted since consolidation.
  const mediaById = useMemo(() => {
    const map = new Map<number, IMedia>();
    (sourceMedia || []).forEach((m) => {
      if (typeof m?.id === "number") map.set(m.id, m);
    });
    return map;
  }, [sourceMedia]);

  // Lookup attached-image description by mediaId for the mixed attachments
  // list (PDFs don't have descriptions; only images do).
  const descriptionByMediaId = useMemo(() => {
    const map = new Map<number, { description: string; failed: boolean }>();
    attached.forEach((img) => {
      map.set(img.mediaId, {
        description: img.description || "",
        failed: !!img.failed,
      });
    });
    return map;
  }, [attached]);

  // Parallel lookup for PDF summaries (one-line, generated at consolidation).
  // Entries with failed=true still carry a non-empty `summary` (fallback to
  // "<filename> — <first sentence>"), so the render branch is always non-empty.
  const summaryByMediaId = useMemo(() => {
    const map = new Map<number, { summary: string; failed: boolean }>();
    (plan.attachedDocs || []).forEach((doc) => {
      map.set(doc.mediaId, {
        summary: doc.summary || "",
        failed: !!doc.failed,
      });
    });
    return map;
  }, [plan.attachedDocs]);

  const styleName =
    scenesWithMeta.find((s) => s.generationMetadata?.skyboxStyleName)?.generationMetadata
      ?.skyboxStyleName || null;

  const isGenerating = phase === "generating";
  const isGenerated = phase === "generated";
  const isErrored = isGenerated && erroredSkyboxes.length > 0;
  const anyScenePending = imageScenes.some((s) => s.generationMetadata?.status === "pending");

  // Effective start: prefer the in-memory timestamp captured when the user
  // kicked off generation; fall back to the earliest server-stamped
  // submittedAt across scenes so the elapsed chip survives a page reload.
  const earliestSubmittedAtMs = useMemo(() => {
    const stamps = imageScenes
      .map((s) =>
        s.generationMetadata?.submittedAt
          ? new Date(s.generationMetadata.submittedAt).getTime()
          : null,
      )
      .filter((v): v is number => v !== null && Number.isFinite(v));
    return stamps.length > 0 ? Math.min(...stamps) : null;
  }, [imageScenes]);
  const effectiveStartedAt = generationStartedAt ?? earliestSubmittedAtMs;

  // Tick once a second while generating so the elapsed chip stays live.
  // No timer when not generating (cheap idle state) or when start ts unknown.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (phase !== "generating" || !effectiveStartedAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [phase, effectiveStartedAt]);

  const elapsedMs = effectiveStartedAt ? Math.max(0, now - effectiveStartedAt) : null;

  const bannerLabel = isGenerating
    ? t(`${gr}.bannerGenerating`)
    : isErrored
    ? t(`${gr}.bannerGeneratedWithErrors`, { n: erroredSkyboxes.length })
    : t(`${gr}.bannerGenerated`);

  // Banner accent: purple while generating (matches existing plan-mode progress
  // pill), green at success, amber when generated with errors. All three
  // already exist in the codebase — no new palette introduced.
  const bannerAccent = isGenerating
    ? { ring: "border-purple-200", bg: "bg-purple-50", text: "text-purple-700" }
    : isErrored
    ? { ring: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700" }
    : { ring: "border-green-200", bg: "bg-green-50", text: "text-green-700" };

  const scenarioMarkdown = useMemo(() => serializePlanToScenarioMarkdown(plan), [plan]);
  const graphKey = useMemo(() => generateHashFromString(scenarioMarkdown), [scenarioMarkdown]);

  const [graphOpen, setGraphOpen] = useState(false);
  const [refsOpen, setRefsOpen] = useState(false);
  const [scenesOpen, setScenesOpen] = useState(false);
  const [tokensOpen, setTokensOpen] = useState(false);

  // Recap data: fetched lazily when user first expands an interaction
  // drill-down in any scene. Memoized so all scenes share one fetch.
  const [recapData, setRecapData] = useState<RecapScene[] | null>(null);
  const [recapLoading, setRecapLoading] = useState(false);

  const fetchRecapData = useCallback(async () => {
    if (recapData || recapLoading || !lastGeneratedProjectGroupId) return;
    setRecapLoading(true);
    try {
      const jwt = localStorage.getItem("wixar.auth.jwt");
      const resp = await fetch(
        `${baseUrl}/api/gpt/plan-mode/scenaria-recap/${lastGeneratedProjectGroupId}`,
        { headers: { Authorization: `Bearer ${jwt}` } },
      );
      if (resp.ok) {
        const json = await resp.json();
        setRecapData(json.data || []);
      }
    } catch {
      // Non-fatal
    } finally {
      setRecapLoading(false);
    }
  }, [recapData, recapLoading, lastGeneratedProjectGroupId]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      <div
        className={`flex items-center gap-3 rounded-md border ${bannerAccent.ring} ${bannerAccent.bg} px-3 py-2`}
      >
        {isGenerating ? (
          <div
            className="w-5 h-5 shrink-0 rounded-full border-2 border-purple-200 border-t-purple-500 animate-spin"
            aria-label={t(`${gr}.bannerGenerating`)}
          />
        ) : (
          <div
            className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              isErrored ? "bg-amber-200 text-amber-800" : "bg-green-200 text-green-800"
            }`}
          >
            {isErrored ? "!" : "✓"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${bannerAccent.text}`}>{bannerLabel}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-600 mt-0.5">
            {scenesWithMeta.length > 0 && (
              <span>
                {t(`${gr}.skyboxesProgress`, {
                  done: completedSkyboxes.length,
                  total: scenesWithMeta.length,
                })}
                {erroredSkyboxes.length > 0 && (
                  <span className="text-red-600">
                    {" "}
                    · {t(`${gr}.skyboxesFailed`, { n: erroredSkyboxes.length })}
                  </span>
                )}
              </span>
            )}
            {attached.length > 0 && (
              <span>
                {t(`${gr}.refsAnalyzed`, { n: successfulRefs.length })}
                {failedRefs.length > 0 && (
                  <span className="text-red-600">
                    {" "}
                    · {t(`${gr}.refsFailed`, { n: failedRefs.length })}
                  </span>
                )}
              </span>
            )}
            {styleName && (
              <span>
                {t(`${gr}.style`)} : {styleName}
              </span>
            )}
            {isGenerating && elapsedMs != null && (
              <span>{t(`${gr}.generationElapsed`, { duration: formatDuration(elapsedMs) })}</span>
            )}
            {isGenerating && progressMessage && (
              <span className="text-purple-700">{progressMessage}</span>
            )}
          </div>
          {imageScenes.length > 0 && (
            <div className="text-[11px] text-gray-600 italic mt-1">
              {attached.length > 0
                ? t(`${gr}.unityOfLocationWith`, { n: attached.length })
                : t(`${gr}.unityOfLocationWithout`)}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <button
            type="button"
            onClick={() => setGraphOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left text-xs font-semibold text-gray-700 hover:text-gray-900"
          >
            <span>{t(`${gr}.scenarioGraph`)}</span>
            <span className="text-gray-400">{graphOpen ? "▴" : "▾"}</span>
          </button>
          {graphOpen && (
            <div className="mt-2 rounded border border-gray-100 bg-gray-50 py-2">
              <AIGeneratedGraph markdown={scenarioMarkdown} mdStringId={graphKey} />
            </div>
          )}
        </div>

        {scenesWithMeta.length > 0 || imageScenes.length > 0 ? (
          <div>
            <button
              type="button"
              onClick={() => setScenesOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left text-xs font-semibold text-gray-700 hover:text-gray-900"
            >
              <span className="inline-flex items-center gap-2">
                {t(`${gr}.sectionScenes`)}
                {anyScenePending && (
                  <span
                    className="inline-block w-2.5 h-2.5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"
                    aria-hidden
                  />
                )}
              </span>
              <span className="text-gray-400">{scenesOpen ? "▴" : "▾"}</span>
            </button>
            {scenesOpen && (
              <div className="mt-2 space-y-2">
                {plan.sceneOutline.map((scene, i) => (
                  <SceneDetails
                    key={scene.sceneId || `scene-${i}`}
                    scene={scene}
                    sceneIndex={i}
                    plan={plan}
                    mediaById={mediaById}
                    phase={phase}
                    recapData={recapData}
                    recapLoading={recapLoading}
                    onRequestRecap={fetchRecapData}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}

        {sourceMedia.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setRefsOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left text-xs font-semibold text-gray-700 hover:text-gray-900"
            >
              <span>
                {t(`${gr}.sectionAttachments`)}{" "}
                <span className="text-gray-400 font-normal">({sourceMedia.length})</span>
              </span>
              <span className="text-gray-400">{refsOpen ? "▴" : "▾"}</span>
            </button>
            {refsOpen && (
              <ul className="mt-2 space-y-2 text-xs">
                {sourceMedia.map((m) => {
                  const isImg = isImageMedia(m);
                  const label = mediaLabel(m, m.id);
                  const desc = descriptionByMediaId.get(m.id);
                  return (
                    <li key={`attachment-${m.id}`} className="flex items-start gap-2">
                      <span
                        className={`shrink-0 mt-0.5 ${isImg ? "text-purple-600" : "text-red-600"}`}
                        aria-hidden
                      >
                        {isImg ? <MdImage size={16} /> : <MdPictureAsPdf size={16} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-gray-800 font-medium truncate">{label}</div>
                        {isImg ? (
                          desc?.failed ? (
                            <div className="text-red-600">{t(`${gr}.refFailed`, { id: m.id })}</div>
                          ) : desc && desc.description ? (
                            <div className="text-gray-600">
                              {desc.description.length > 240
                                ? `${desc.description.slice(0, 240)}…`
                                : desc.description}
                            </div>
                          ) : (
                            <div className="text-gray-400 italic">
                              {t(`${gr}.attachmentImageDescriptionMissing`)}
                            </div>
                          )
                        ) : (
                          (() => {
                            const docInfo = summaryByMediaId.get(m.id);
                            if (docInfo && docInfo.summary) {
                              const text =
                                docInfo.summary.length > 240
                                  ? `${docInfo.summary.slice(0, 240)}…`
                                  : docInfo.summary;
                              return <div className="text-gray-600">{text}</div>;
                            }
                            return (
                              <div className="text-gray-500 italic">
                                {t(`${gr}.attachmentPdfNote`)}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {plan.tokenUsage && (plan.tokenUsage.inputTokens || plan.tokenUsage.outputTokens) ? (
          <div>
            <button
              type="button"
              onClick={() => setTokensOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left text-xs font-semibold text-gray-700 hover:text-gray-900"
            >
              <span>{t(`${gr}.sectionTokens`)}</span>
              <span className="text-gray-400">{tokensOpen ? "▴" : "▾"}</span>
            </button>
            {tokensOpen && (
              <p className="mt-1 text-[11px] text-gray-500">
                {t(`${gr}.tokensUsed`, {
                  inTokens: formatNumber(plan.tokenUsage.inputTokens || 0),
                  outTokens: formatNumber(plan.tokenUsage.outputTokens || 0),
                })}
              </p>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
        {lastGeneratedProjectGroupId ? (
          <button
            type="button"
            className="btn-primary-fill text-sm disabled:opacity-60 disabled:cursor-wait"
            onClick={onOpenModule}
            disabled={isOpeningModule || isGenerating}
          >
            {isOpeningModule ? t(`${gr}.openingModule`) : t(`${gr}.openModule`)}
          </button>
        ) : null}
        <button
          type="button"
          className="btn-alternative-outline text-sm disabled:opacity-60"
          onClick={onNewScenario}
          disabled={isGenerating}
        >
          {t(`${gr}.newScenario`)}
        </button>
      </div>
    </div>
  );
};

export default GenerationRecap;
